import React, { useCallback, useEffect, useMemo } from "react";
import { getNetworkInfoThunk } from "tm-shared/api";
import { Contract, Point } from "tm-shared/types";
import {
    formatDate,
    isPointChains,
    isPointDistribution,
} from "tm-shared/utils";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import { getFileUrl } from "@/utils/file/getFileUrl";
import { handleDownloadFile } from "@/utils/file/handleDownloadFile";

import {
    StyledAPContractTable,
    StyledAPContractTableFile,
    StyledAPContractTableFileIcon,
    StyledAPContractTableFileName,
    StyledAPContractTableRow,
    StyledAPContractTableSplittedBlock,
    StyledAPContractTableSplittedRow,
} from "../../../Styled";
import { ContractAdditionalInfo } from "./ContractAdditionalInfo";

interface ContractViewProps {
    contract: Contract;
    fopName: string;
    contractTypeName: string;
    point: Point | null;
}

export const ContractView: React.FC<ContractViewProps> = ({
    contract,
    fopName,
    contractTypeName,
    point,
}) => {
    const { regions, networks } = useAppSelector((s) => s.user);

    const dispatch = useAppDispatch();

    const startDate = useMemo(
        () => formatDate(contract.startDate),
        [contract.startDate],
    );

    const endDate = useMemo(
        () => (contract.endDate ? formatDate(contract.endDate) : "Безстроково"),
        [contract.endDate],
    );

    const getTargetTypeInfo = useCallback(
        (type: string): string => {
            if (!point) return "";

            const {
                market: { building, contragent },
            } = point ?? {};

            const networkInfo: Record<string, string> = isPointChains(point)
                ? {
                      network: `Мережа (${
                          networks?.[point.typeContext?.networkId || ""] ??
                          "Невідома мережа"
                      })`,
                  }
                : {};
            const distributionInfo: Record<string, string> =
                isPointDistribution(point)
                    ? {
                          storage: `Склад (${
                              point.typeContext?.storage?.name ??
                              "Невідомий склад"
                          })`,
                      }
                    : {};

            const mapped: Record<string, string> = {
                contragent: `Контрагент (${
                    contragent?.name || "Невідома юр.особа"
                })`,
                area: `Область (${
                    regions?.[building.address?.areaId || ""] ??
                    "Невідомий регіон"
                })`,

                ...networkInfo,
                ...distributionInfo,
            };

            return mapped[type] || "ТТ";
        },
        [point, regions, networks],
    );

    const handleFileClick = useCallback((file: string) => {
        const fileName = file.split("/").pop() || "document";
        handleDownloadFile(getFileUrl(file), fileName.replace(/\..+$/, ""));
    }, []);

    const dateBlocks = useMemo(
        () => [
            { label: "Дата початку:", value: startDate },
            { label: "Дата завершення:", value: endDate },
        ],
        [startDate, endDate],
    );

    useEffect(() => {
        if (
            point &&
            isPointChains(point) &&
            !networks?.[point.typeContext.networkId]
        ) {
            dispatch(
                getNetworkInfoThunk({ network: point.typeContext.networkId }),
            );
        }
    }, [dispatch, point, networks]);

    return (
        <StyledAPContractTable>
            <StyledAPContractTableRow>
                <PrimaryText fontSize={16}>{fopName}</PrimaryText>
                {contract?.description && (
                    <SecondaryText>{contract.description}</SecondaryText>
                )}
            </StyledAPContractTableRow>

            <StyledAPContractTableRow>
                <StyledAPContractTableSplittedRow>
                    {dateBlocks.map(({ label, value }) => (
                        <StyledAPContractTableSplittedBlock key={label}>
                            <SecondaryText>{label}</SecondaryText>
                            <PrimaryText fontSize={16}>{value}</PrimaryText>
                        </StyledAPContractTableSplittedBlock>
                    ))}
                </StyledAPContractTableSplittedRow>
            </StyledAPContractTableRow>

            <StyledAPContractTableRow>
                <SecondaryText>Тип контракту:</SecondaryText>
                <PrimaryText fontSize={16}>{contractTypeName}</PrimaryText>
            </StyledAPContractTableRow>

            <StyledAPContractTableRow>
                <SecondaryText>Рівень дії:</SecondaryText>
                <PrimaryText fontSize={16}>
                    {getTargetTypeInfo(contract.targetType)}
                </PrimaryText>
            </StyledAPContractTableRow>

            <StyledAPContractTableRow>
                <SecondaryText>Документи:</SecondaryText>

                {contract?.files && contract?.files.length > 0 ? (
                    contract.files.map((file) => {
                        const fileName = file.split("/").pop()!;
                        return (
                            <StyledAPContractTableFile
                                key={file}
                                onClick={() => handleFileClick(file)}
                            >
                                <StyledAPContractTableFileIcon />
                                <StyledAPContractTableFileName>
                                    {fileName}
                                </StyledAPContractTableFileName>
                            </StyledAPContractTableFile>
                        );
                    })
                ) : (
                    <PrimaryText fontSize={16}>Файли відсутні</PrimaryText>
                )}
            </StyledAPContractTableRow>

            <ContractAdditionalInfo contract={contract} />
        </StyledAPContractTable>
    );
};
